import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'

interface AgentCardProps {
  to: string
  logo: string
  logoAlt: string
  title: string
  description?: string
  accentColor: 'iris' | 'atlas' | 'cronos' | 'talos'
  disabled?: boolean
}

const accentClasses: Record<
  AgentCardProps['accentColor'],
  { icon: string; text: string; focusRing: string; cardHover: string }
> = {
  iris: {
    icon: 'bg-secondary-shell/10 group-hover:bg-secondary-shell/20',
    text: 'text-secondary-shell',
    focusRing: 'focus-visible:ring-secondary-shell',
    cardHover: 'group-hover:bg-secondary-shell/5',
  },
  atlas: {
    icon: 'bg-success/10 group-hover:bg-success/20',
    text: 'text-success',
    focusRing: 'focus-visible:ring-success',
    cardHover: 'group-hover:bg-success/5',
  },
  cronos: {
    icon: 'bg-pink/10 group-hover:bg-pink/20',
    text: 'text-pink',
    focusRing: 'focus-visible:ring-pink',
    cardHover: 'group-hover:bg-pink/5',
  },
  talos: {
    icon: 'bg-violet/10 group-hover:bg-violet/20',
    text: 'text-violet',
    focusRing: 'focus-visible:ring-violet',
    cardHover: 'group-hover:bg-violet/5',
  },
}

export function AgentCard({ to, logo, logoAlt, title, description, accentColor, disabled }: AgentCardProps) {
  const accent = accentClasses[accentColor]

  return (
    <Link
      to={to as never}
      disabled={disabled}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      className={`flex w-full h-full rounded-2xl outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-offset-background-primary ${accent.focusRing} transition-shadow ${disabled ? 'pointer-events-none' : ''}`}
    >
      <motion.div
        tabIndex={-1}
        className={`group relative flex w-full h-full items-center gap-4 px-10 py-6 bg-background-elevated/80 border-2 border-secondary-shell/10 rounded-2xl text-text-primary text-lg font-semibold shadow-xl overflow-hidden transition-colors ${disabled ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}`}
        whileHover={disabled ? undefined : { scale: 1.03, boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.2)' }}
        whileTap={disabled ? undefined : { scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        <div
          className={`absolute inset-0 bg-linear-to-r from-primary-shell/10 to-transparent opacity-0 ${disabled ? '' : `group-hover:opacity-100`} transition-opacity duration-300 ${accent.cardHover}`}
        />
        <div
          className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-xl  ${accent.icon} transition-colors`}
        >
          <img src={logo} alt={logoAlt} className="w-7" />
        </div>
        <div className="relative z-10 text-left flex-1">
          <div className={`text-lg font-bold ${accent.text} transition-colors`}>{title}</div>
          {description && <div className="text-sm font-normal text-text-secondary">{description}</div>}
        </div>
      </motion.div>
    </Link>
  )
}
